import { ReactElement, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from '@apollo/client'
import TagManager from 'react-gtm-module'
import { Layout } from '@/components/Layout'
import { Container } from '@/components/utils'
import { getProvincesWithCities } from '@/src/locations'
import { getActivities, getActivity } from '@/src/activities'
import { Input } from '@/components/elements/Input'
import { Checkbox } from '@/components/elements/Checkbox'
import { Select } from '@/components/elements/Select'
import { getYarRange } from '../../../utils/getYearRange'
import { CreateRegistrationMutationVariables } from '../../../types/types'
import CREATE_REGISTRATION_MUTATION from '../../../apollo/mutations/createRegistrationMutation.graphql'
import SubmitButton from '@/components/elements/Button/submit'
import { Alert } from '@/components/elements/Alert'
import { Dialog } from '@/components/elements/Alert/dialog'
import { Link } from '@/components/elements/Link'
import { isValidEmail } from '@/utils/isValidEmail'
import { CheckboxGroup } from '@/components/elements/Checkbox/group'

export async function getStaticPaths() {
  const activities = getActivities()

  const paths = activities.map((activity) => ({
    params: { id: activity.id },
  }))

  return { paths, fallback: false }
}
export async function getStaticProps({ params }) {
  const activity = getActivity(params.id)

  const [provinces, provincesWithCities] = getProvincesWithCities()

  const { title } = activity
  const description = `Volunteer for ${title}`
  return {
    props: {
      meta: {
        title,
        description,
      },
      activity,
      provinces,
      provincesWithCities,
    },
  }
}

export default function VolunteerPage({
  activity,
  provinces,
  provincesWithCities,
  meta,
}): ReactElement {
  const isMemberOptions = [
    {
      label: 'Sí',
      value: 'true',
    },
    {
      label: 'No',
      value: 'false',
    },
  ]
  const isVolunteerOptions = [
    {
      label: 'Sí',
      value: 'true',
    },
    {
      label: 'No',
      value: 'false',
    },
  ]

  const genderOptions = [
    {
      label: 'Dona',
      value: 'Dona',
    },
    {
      label: 'No binari',
      value: 'NoBinari',
    },
    {
      label: 'Home',
      value: 'Home',
    },
  ]

  const [cities, setCities] = useState([])

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm()

  const provincesWithCitiesMap = new Map<string, []>(provincesWithCities)

  const selectedProvince = watch('province')

  useEffect(() => {
    setValue('city', '', { shouldValidate: false })
    if (!selectedProvince) {
      setCities([])
      return
    }
    const citiesFromProvince = provincesWithCitiesMap.get(selectedProvince)
    setCities(citiesFromProvince)
  }, [selectedProvince])

  const [errorMessages, setErrorMessages] = useState('')
  const [createRegistration, registrationState] =
    useMutation<CreateRegistrationMutationVariables>(
      CREATE_REGISTRATION_MUTATION,
      {
        onError: ({ networkError, graphQLErrors }) => {
          if (networkError) {
            setErrorMessages('there has been a network error')
          }
          if (graphQLErrors) {
            setErrorMessages(graphQLErrors[0].message)
          }
        },
      }
    )
  const { data: registrationResponseData, loading, error } = registrationState

  const submitRegistration = (formData) => {
    const { name, surname, gender, phone, email, province, city } = formData
    const birthYear = parseInt(formData.birthYear, 10)
    const isLanguageVolunteer = formData.isLanguageVolunteer === 'true'
    const isMember = formData.isMember === 'true'
    const activityId = activity.id
    createRegistration({
      variables: {
        createRegistrationData: {
          activity: activityId,
          name,
          surname,
          gender,
          birthYear,
          province,
          city,
          phone,
          email,
          isLanguageVolunteer,
          isMember,
          type: 'volunteer',
        },
      },
    }).then(() => {
      TagManager.dataLayer({
        dataLayer: {
          event: 'lead',
          form: 'form-lead-tandem-voluntari',
        },
      })
    })
  }

  const showSuccess = registrationResponseData

  useEffect(() => {
    if (showSuccess) window.scrollTo(0, 0)
  }, [showSuccess])

  return (
    <Layout meta={meta}>
      <div className="py-12 bg-beige">
        <Container>
          {!showSuccess && (
            <div>
              <div className="mx-auto pt-8 max-w-md text-center">
                <h1 className="text-2xl">
                  Inscriu-te com a voluntari a l’activitat
                </h1>
                <p className="py-4 font-body text-base leading-6">
                  Completa el següent formulari i rebràs més detalls del procés
                  per correu electrònic
                </p>
              </div>
              <form
                onSubmit={handleSubmit(submitRegistration)}
                className="mb-4 mx-auto px-4 py-4 max-w-lg text-navy-900 font-body bg-white rounded-lg md:px-10 md:py-10"
              >
                <Input
                  id="first-name"
                  label="Nom"
                  name="name"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  register={register}
                  errors={errors}
                />
                <Input
                  className="mt-4"
                  id="surname"
                  label="Cognoms"
                  name="surname"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  register={register}
                  errors={errors}
                />
                <Input
                  className="mt-4"
                  id="email-input"
                  label="Correu electrònic"
                  name="email"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                    validate: isValidEmail,
                  }}
                  register={register}
                  errors={errors}
                />
                <Input
                  className="mt-4"
                  id="phone-input"
                  label="Telèfon"
                  name="phone"
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  register={register}
                  errors={errors}
                />
                <CheckboxGroup
                  title="Amb quin gènere t’identifiques més?"
                  errors={errors}
                  name="gender"
                >
                  {genderOptions.map((genderOption) => (
                    <Checkbox
                      key={`gender${genderOption.value}`}
                      className="mt-4"
                      name="gender"
                      type="radio"
                      register={register}
                      id={`gender${genderOption.value}`}
                      value={genderOption.value}
                      label={genderOption.label}
                      validationRules={{
                        required: 'Empleneu aquest camp',
                      }}
                    />
                  ))}
                </CheckboxGroup>
                <Select
                  className="mt-4"
                  options={provinces.map((province) => ({
                    label: province,
                    value: province,
                  }))}
                  id="provincia"
                  label="Província"
                  defaultOptionLabel="Província"
                  defaultValue=""
                  name="province"
                  register={register}
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  errors={errors}
                />
                <Select
                  className="mt-4"
                  options={cities.map((city) => ({
                    label: city,
                    value: city,
                  }))}
                  id="city"
                  label="Població"
                  defaultOptionLabel="Població"
                  defaultValue=""
                  name="city"
                  register={register}
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  errors={errors}
                />
                <Select
                  className="mt-4"
                  options={getYarRange(100).map((option) => ({
                    label: `${option}`,
                    value: `${option}`,
                  }))}
                  id="birthYear"
                  label="Any de naixement"
                  defaultOptionLabel=""
                  name="birthYear"
                  register={register}
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  errors={errors}
                />
                <Select
                  className="mt-4"
                  options={isMemberOptions.map(({ label, value }) => ({
                    label,
                    value,
                  }))}
                  id="isMember"
                  label="Ets soci d’Òmnium Cutural?"
                  defaultOptionLabel=""
                  name="isMember"
                  register={register}
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  errors={errors}
                />
                <Select
                  className="mt-4"
                  options={isVolunteerOptions.map(({ label, value }) => ({
                    label,
                    value,
                  }))}
                  id="is-volunteer"
                  label="Estàs inscrit/a al Voluntariat per la Llengua?"
                  defaultOptionLabel=""
                  name="isLanguageVolunteer"
                  register={register}
                  validationRules={{
                    required: 'Empleneu aquest camp',
                  }}
                  errors={errors}
                />
                <CheckboxGroup errors={errors} name="acceptPrivacy">
                  <Checkbox
                    name="acceptPrivacy"
                    register={register}
                    id="privacy-policy"
                    label="Accepto la política de privacitat"
                    description={<PrivacyPolicy />}
                    className=""
                    validationRules={{
                      required: 'Empleneu aquest camp',
                    }}
                  />
                </CheckboxGroup>

                <div className="mt-4">
                  <SubmitButton
                    className="w-full"
                    label="Envia"
                    isLoading={loading}
                  />
                </div>
                {error && (
                  <Alert
                    type="error"
                    className="mt-6"
                    title="Error en el formulari"
                    description={errorMessages}
                  />
                )}
              </form>
            </div>
          )}
          {registrationResponseData && (
            <Dialog
              title="Formulari enviat!"
              description="Gràcies per omplir-lo i enviar-nos-el. Revisa la teva safata d’entrada per a més detalls."
              type="success"
            />
          )}
        </Container>
      </div>
    </Layout>
  )
}

export const PrivacyPolicy = (): JSX.Element => {
  return (
    <ul className="mt-2 text-navy-100">
      <li>
        <strong>Responsable: </strong>
        Òmnium Cultural.
      </li>
      <li>
        <strong>Finalitats: </strong>
        Gestionar la teva inscripció al projecte Tàndem i fer-te arribar
        informació del projecte i d’Òmnium Cultural.
      </li>
      <li>
        <strong>Drets: </strong>
        Accés, rectificació, supressió, oposició, limitació i portabilitat de
        dades.
      </li>
      <li>
        <strong>Més informació: </strong>
        Trobaràs informació addicional i més detallada a la{' '}
        <Link
          href="/politica-de-privacitat"
          className="text-navy-100"
          variant="no-variant"
          target="_blank"
        >
          Política de Privacitat
        </Link>
        .
      </li>
    </ul>
  )
}
